import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Switch,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Link,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PlayArrow as RunIcon,
  Pause as PauseIcon,
  Schedule as CronIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  OpenInNew as OpenIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  YouTube as YouTubeIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Public as WebsiteIcon,
  Language as PlanetIcon,
  CleaningServices as CleanIcon,
  ArrowDropDown as ArrowIcon
} from '@mui/icons-material';
import api from '../../../../services/api';
import {
  operationalElevation,
  operationalFeedback,
  operationalRadii,
  operationalSizing,
  operationalStatusColors
} from '../../../../design-system/tokens';

const DorkDiscoveryTab = ({ brandId, onPushToScraper }) => {
  const surfaceSx = {
    borderRadius: `${operationalRadii.surface}px`,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    boxShadow: operationalElevation.none
  };
  const insetSurfaceSx = {
    ...surfaceSx,
    bgcolor: 'background.default'
  };
  const buttonSx = {
    minHeight: operationalSizing.compact,
    borderRadius: `${operationalRadii.control}px`,
    fontWeight: 650,
    textTransform: 'none',
    boxShadow: operationalElevation.none,
    '&:hover': {
      boxShadow: operationalElevation.none
    }
  };
  const alertSx = (severity = 'info') => ({
    borderRadius: `${operationalRadii.control}px`,
    border: '1px solid',
    borderLeft: `${operationalFeedback.alert.accentBorderWidth}px solid ${operationalStatusColors[severity] || operationalStatusColors.info}`,
    borderColor: 'divider',
    bgcolor: 'background.paper',
    color: 'text.primary',
    boxShadow: operationalElevation.none,
    '& .MuiAlert-icon': {
      color: operationalStatusColors[severity] || operationalStatusColors.info
    }
  });
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [runningJob, setRunningJob] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Form states
  const [showAdd, setShowAdd] = useState(false);
  const [newJob, setNewJob] = useState({
    name: '',
    targetDomain: '',
    keywords: '',
    cronSchedule: '0 3 * * *',
    requiresApproval: true,
    timePeriod: 'all',
    type: 'website',
    platform: 'youtube',
    handle: '',
    selectedBriefingKeywords: [],
    useBriefingKeywords: false
  });
  const [editingJob, setEditingJob] = useState(null);
  const [briefingData, setBriefingData] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);

  // Results Modal states
  const [resultsModalOpen, setResultsModalOpen] = useState(false);
  const [viewingJob, setViewingJob] = useState(null);
  const [results, setResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [selectedResults, setSelectedResults] = useState([]);
  const [processingResults, setProcessingResults] = useState(false);

  // Cleanup Menu states
  const [cleanMenuAnchor, setCleanMenuAnchor] = useState(null);
  const [cleanJobId, setCleanJobId] = useState(null);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/discovery');
      if (response.data.success) {
        setJobs(response.data.data.filter(j => 
            (j.brand && j.brand._id === brandId) || j.brand === brandId
        ));
      }
    } catch (err) {
      setError('Erro ao carregar rotinas de descoberta.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBriefing = async () => {
    setBriefingLoading(true);
    try {
      const response = await api.get('/briefing');
      if (response.data.success) {
        setBriefingData(response.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar briefing:', err);
    } finally {
      setBriefingLoading(false);
    }
  };

  useEffect(() => {
    if (brandId) {
       fetchJobs();
       fetchBriefing();
    }
  }, [brandId]);

  const handleSave = async () => {
    // Validação inteligente baseada no tipo
    const isWebsite = !newJob.type || newJob.type === 'website';
    const hasBriefingSelection = newJob.useBriefingKeywords && newJob.selectedBriefingKeywords?.length > 0;
    const hasKeywords = newJob.keywords || hasBriefingSelection;

    const hasRequiredFields = newJob.name && hasKeywords && (
      (isWebsite && newJob.targetDomain) || 
      (!isWebsite && newJob.platform && newJob.handle)
    );

    if (!hasRequiredFields) {
      setError('Por favor, preencha todos os campos obrigatórios.');
      return;
    }
    
    setLoading(true);
    try {
      if (editingJob) {
        // Modo Edição
        await api.put(`/admin/discovery/${editingJob}`, newJob);
        setSuccessMsg('Rotina atualizada com sucesso!');
      } else {
        // Modo Criação
        await api.post('/admin/discovery', {
          ...newJob,
          brandId
        });
        setSuccessMsg('Rotina criada com sucesso!');
      }
      
      setNewJob({ 
        name: '', 
        targetDomain: '', 
        keywords: '', 
        cronSchedule: '0 3 * * *',
        requiresApproval: true,
        timePeriod: 'all',
        type: 'website',
        platform: 'youtube',
        handle: '',
        selectedBriefingKeywords: [],
        useBriefingKeywords: false
      });
      setEditingJob(null);
      setShowAdd(false);
      fetchJobs();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar rotina.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (job) => {
    setEditingJob(job._id);
    setNewJob({
      name: job.name,
      targetDomain: job.targetDomain || '',
      keywords: job.keywords,
      cronSchedule: job.cronSchedule,
      requiresApproval: job.requiresApproval,
      timePeriod: job.timePeriod || 'all',
      type: job.type || 'website',
      platform: job.platform || 'youtube',
      handle: job.handle || '',
      selectedBriefingKeywords: job.selectedBriefingKeywords || [],
      useBriefingKeywords: job.useBriefingKeywords || false
    });
    setShowAdd(true);
    // Scroll suave para o formulário
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleToggle = async (id) => {
    try {
      await api.put(`/admin/discovery/${id}/toggle`);
      fetchJobs();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta rotina?')) return;
    try {
      await api.delete(`/admin/discovery/${id}`);
      fetchJobs();
      setSuccessMsg('Rotina removida.');
    } catch (err) {
       console.error(err);
    }
  };

  const handleRunNow = async (id) => {
    setRunningJob(id);
    setError(null);
    try {
      const response = await api.post(`/admin/discovery/${id}/run`);
      if (response.data.success) {
        setSuccessMsg(response.data.message || 'Busca concluída!');
        fetchJobs();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao executar busca manual.');
    } finally {
      setRunningJob(null);
    }
  };

  // ── Gerenciamento de Resultados ──
  const fetchResults = async (jobId) => {
    setResultsLoading(true);
    try {
      const response = await api.get(`/admin/discovery/results/${jobId}?status=pending`);
      if (response.data.success) {
        setResults(response.data.data);
      }
    } catch (err) {
      setError('Erro ao carregar resultados pendentes.');
    } finally {
      setResultsLoading(false);
    }
  };

  const handleOpenResults = async (job) => {
    setViewingJob(job);
    setResultsModalOpen(true);
    setSelectedResults([]);
    fetchResults(job._id);
  };

  const handleSelectResult = (id) => {
    setSelectedResults(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedResults(results.map(r => r._id));
    } else {
      setSelectedResults([]);
    }
  };

  // ── Sincronização e Ordenação ──
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('snippet');

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Converte "há 2 dias", "há 1 ano", etc em pesos numéricos comparáveis (minutos)
  const parseYouTubeDate = (text) => {
    if (!text) return Infinity;
    const lower = text.toLowerCase().trim();
    const match = lower.match(/(\d+)/);
    const num = match ? parseInt(match[1]) : 1;
    
    if (lower.includes('segundo')) return num / 60;
    if (lower.includes('minuto')) return num;
    if (lower.includes('hora')) return num * 60;
    if (lower.includes('dia')) return num * 1440;
    if (lower.includes('semana')) return num * 10080;
    if (lower.includes('mês') || lower.includes('meses')) return num * 43200;
    if (lower.includes('ano')) return num * 525600;
    
    return Infinity;
  };

  const getSortedResults = () => {
    return [...results].sort((a, b) => {
      let valA, valB;
      
      if (orderBy === 'snippet') {
        valA = parseYouTubeDate(a.snippet);
        valB = parseYouTubeDate(b.snippet);
      } else if (orderBy === 'foundAt') {
        valA = new Date(a.foundAt).getTime();
        valB = new Date(b.foundAt).getTime();
      } else {
        valA = a[orderBy] || '';
        valB = b[orderBy] || '';
      }

      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleApproveSelected = async () => {
    if (!selectedResults.length || !viewingJob) return;
    setProcessingResults(true);
    try {
      // Usar a nova rota que move para o scraper manual em vez de rodar em background
      const response = await api.post('/admin/discovery/results/move-to-scraper', {
        resultIds: selectedResults
      });
      
      const { urls } = response.data.data;
      
      setSuccessMsg(`${selectedResults.length} links movidos para a aba de Captura!`);
      
      // Se tiver o callback, envia os URLs para o componente pai
      if (onPushToScraper && urls && urls.length > 0) {
        onPushToScraper(urls);
        setResultsModalOpen(false); // Fecha o modal de revisão para ver a aba de captura
      }

      setResults(prev => prev.filter(r => !selectedResults.includes(r._id)));
      setSelectedResults([]);
      fetchJobs();
    } catch (err) {
      setError('Erro ao enviar links para a aba de Captura.');
    } finally {
      setProcessingResults(false);
    }
  };

  const handleRejectSelected = async () => {
    if (!viewingJob || !selectedResults.length) return;
    
    setProcessingResults(true);
    try {
      await api.post(`/admin/discovery/results/reject`, { resultIds: selectedResults });
      setSuccessMsg(`${selectedResults.length} resultados rejeitados.`);
      setSelectedResults([]);
      fetchResults(viewingJob._id);
    } catch (err) {
      setError('Erro ao rejeitar resultados.');
    } finally {
      setProcessingResults(false);
    }
  };

  const handleOpenCleanMenu = (event, jobId) => {
    setCleanMenuAnchor(event.currentTarget);
    setCleanJobId(jobId);
  };

  const handleCloseCleanMenu = () => {
    setCleanMenuAnchor(null);
    setCleanJobId(null);
  };

  const handleCleanHistory = async (status, hours) => {
    if (!cleanJobId) return;
    
    setLoading(true);
    try {
      const response = await api.delete(`/admin/discovery/${cleanJobId}/history?status=${status}&hours=${hours}`);
      if (response.data.success) {
        setSuccessMsg(response.data.message);
        fetchJobs(); // Atualizar contadores
      }
    } catch (err) {
      setError('Erro ao limpar histórico.');
    } finally {
      setLoading(false);
      handleCloseCleanMenu();
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Motor de Descoberta DorkDigger</Typography>
        <Button 
          variant="contained" 
          startIcon={showAdd ? <PauseIcon /> : <AddIcon />} 
          onClick={() => {
            if (showAdd) {
               setEditingJob(null);
               setNewJob({ 
                 name: '', 
                 targetDomain: '', 
                 keywords: '', 
                 cronSchedule: '0 3 * * *', 
                 requiresApproval: true, 
                 timePeriod: 'all',
                 type: 'website',
                 platform: 'youtube',
                 handle: '',
                 selectedBriefingKeywords: [],
                 useBriefingKeywords: false
               });
            }
            setShowAdd(!showAdd);
          }}
          color={showAdd ? "inherit" : "primary"}
        >
          {showAdd ? 'Cancelar' : 'Nova Rotina'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ ...alertSx('error'), mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      
      <Snackbar 
        open={!!successMsg} 
        autoHideDuration={4000} 
        onClose={() => setSuccessMsg(null)}
        message={successMsg}
      />

      {showAdd && (
        <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {editingJob ? 'Editar Rotina de Busca' : 'Cadastrar Nova Busca Automática'}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Tipo de Busca</InputLabel>
              <Select
                value={newJob.type || 'website'}
                label="Tipo de Busca"
                onChange={(e) => setNewJob({...newJob, type: e.target.value})}
              >
                <MenuItem value="website">Site ou Portal (Domínio)</MenuItem>
                <MenuItem value="social">Rede Social (Plataforma + Arroba)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <TextField 
              label="Nome da Rotina" 
              placeholder="Ex: Monitor BMW @Youtube"
              value={newJob.name}
              onChange={(e) => setNewJob({...newJob, name: e.target.value})}
              fullWidth
              size="small"
            />
            
            {newJob.type === 'social' ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControl size="small" sx={{ flexGrow: 1 }}>
                  <InputLabel>Plataforma</InputLabel>
                  <Select
                    value={newJob.platform || 'youtube'}
                    label="Plataforma"
                    onChange={(e) => setNewJob({...newJob, platform: e.target.value})}
                  >
                    <MenuItem value="youtube">YouTube</MenuItem>
                    <MenuItem value="instagram">Instagram</MenuItem>
                    <MenuItem value="tiktok">TikTok</MenuItem>
                    <MenuItem value="twitter">X / Twitter</MenuItem>
                    <MenuItem value="facebook">Facebook</MenuItem>
                    <MenuItem value="linkedin">LinkedIn</MenuItem>
                  </Select>
                </FormControl>
                <TextField 
                  label="Perfil / Arroba" 
                  placeholder="Ex: MotoPlay"
                  value={newJob.handle}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (val.startsWith('@')) val = val.substring(1);
                    setNewJob({...newJob, handle: val});
                  }}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <Box sx={{ mr: 0.5, color: 'text.secondary' }}>@</Box>
                  }}
                />
              </Box>
            ) : (
              <TextField 
                label="Domínio do Site" 
                placeholder="Ex: quatrorodas.abril.com.br"
                value={newJob.targetDomain}
                onChange={(e) => setNewJob({...newJob, targetDomain: e.target.value})}
                fullWidth
                size="small"
              />
            )}

            {newJob.type === 'social' && newJob.platform === 'youtube' && (
              <Box sx={{ gridColumn: 'span 2', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={newJob.useBriefingKeywords} 
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked && !briefingData) fetchBriefing();
                        setNewJob({...newJob, useBriefingKeywords: checked});
                      }} 
                    />
                  }
                  label={
                    <Typography variant="body2" color="primary" fontWeight={600}>
                      Sincronizar com Palavras-Chave do Briefing (Selecionar Termos)
                    </Typography>
                  }
                />
                
                {newJob.useBriefingKeywords && (
                  <Paper elevation={0} variant="outlined" sx={{ ...insetSurfaceSx, p: 1.5, mt: 1, borderStyle: 'dashed' }}>
                    {briefingLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption">Carregando termos...</Typography>
                      </Box>
                    ) : !briefingData ? (
                      <Typography variant="caption" color="text.secondary">Nenhum briefing configurado para esta marca.</Typography>
                    ) : (
                      <Box>
                        <Typography variant="caption" fontWeight={700} display="block" gutterBottom>
                          Escolha os termos para monitorar neste canal:
                        </Typography>
                        
                        {/* Principais - Marca */}
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>Sua Marca</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {briefingData.primaryKeywords?.map(k => (
                              <Chip
                                key={k.keyword}
                                label={k.keyword}
                                size="small"
                                onClick={() => {
                                  const selected = newJob.selectedBriefingKeywords || [];
                                  if (selected.includes(k.keyword)) {
                                    setNewJob({...newJob, selectedBriefingKeywords: selected.filter(s => s !== k.keyword)});
                                  } else {
                                    setNewJob({...newJob, selectedBriefingKeywords: [...selected, k.keyword]});
                                  }
                                }}
                                color={newJob.selectedBriefingKeywords?.includes(k.keyword) ? "primary" : "default"}
                                variant={newJob.selectedBriefingKeywords?.includes(k.keyword) ? "filled" : "outlined"}
                                sx={{ fontSize: '0.7rem', height: 24 }}
                              />
                            ))}
                          </Box>
                        </Box>
                        
                        {/* Concorrentes */}
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>Concorrentes / Referências</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {briefingData.competitorKeywords?.map(k => (
                              <Chip
                                key={k.keyword}
                                label={k.keyword}
                                size="small"
                                onClick={() => {
                                  const selected = newJob.selectedBriefingKeywords || [];
                                  if (selected.includes(k.keyword)) {
                                    setNewJob({...newJob, selectedBriefingKeywords: selected.filter(s => s !== k.keyword)});
                                  } else {
                                    setNewJob({...newJob, selectedBriefingKeywords: [...selected, k.keyword]});
                                  }
                                }}
                                color={newJob.selectedBriefingKeywords?.includes(k.keyword) ? "info" : "default"}
                                variant={newJob.selectedBriefingKeywords?.includes(k.keyword) ? "filled" : "outlined"}
                                sx={{ fontSize: '0.7rem', height: 24 }}
                              />
                            ))}
                          </Box>
                        </Box>
                        
                        <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                          <Button 
                            size="small" 
                            variant="text" 
                            sx={{ fontSize: '0.6rem' }}
                            onClick={() => {
                              const allKw = [
                                ...(briefingData.primaryKeywords?.map(k => k.keyword) || []),
                                ...(briefingData.competitorKeywords?.map(k => k.keyword) || [])
                              ];
                              setNewJob({...newJob, selectedBriefingKeywords: allKw});
                            }}
                          >
                            Selecionar Todos
                          </Button>
                          <Button 
                            size="small" 
                            variant="text" 
                            color="inherit"
                            sx={{ fontSize: '0.6rem' }}
                            onClick={() => setNewJob({...newJob, selectedBriefingKeywords: []})}
                          >
                            Limpar
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Paper>
                )}
              </Box>
            )}
          </Box>
          <TextField 
            label="Keywords (Dorks)" 
            placeholder='Ex: "Marca XYZ" OR #MarcaXYZ'
            value={newJob.keywords}
            onChange={(e) => setNewJob({...newJob, keywords: e.target.value})}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField 
              label="Agendamento (Cron)" 
              value={newJob.cronSchedule}
              onChange={(e) => setNewJob({...newJob, cronSchedule: e.target.value})}
              size="small"
              sx={{ width: 140 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Período de Busca</InputLabel>
              <Select
                value={newJob.timePeriod || 'all'}
                label="Período de Busca"
                onChange={(e) => setNewJob({...newJob, timePeriod: e.target.value})}
              >
                <MenuItem value="all">Todo o período</MenuItem>
                <MenuItem value="day">Últimas 24 horas</MenuItem>
                <MenuItem value="week">Última Semana</MenuItem>
                <MenuItem value="month">Último Mês</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox 
                  checked={newJob.requiresApproval} 
                  onChange={(e) => setNewJob({...newJob, requiresApproval: e.target.checked})} 
                />
              }
              label="Requer aprovação"
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button variant="contained" onClick={handleSave} disabled={loading} sx={buttonSx}>
              {editingJob ? 'Salvar Alterações' : 'Salvar Rotina'}
            </Button>
          </Box>
        </Paper>
      )}

      {loading && !jobs.length ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <List>
          {jobs.map((job) => (
            <Paper key={job._id} sx={{ ...surfaceSx, mb: 2, overflow: 'hidden' }} variant="outlined">
              <ListItem sx={{ py: 1.5 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>{job.name}</Typography>
                      <Chip 
                        label={job.isActive ? "Ativa" : "Pausada"} 
                        size="small" 
                        color={job.isActive ? "success" : "default"}
                        variant="outlined"
                      />
                      {job.requiresApproval && (
                        <Chip label="Aprovação Manual" size="small" variant="outlined" color="warning" />
                      )}
                      {job.timePeriod && job.timePeriod !== 'all' && (
                        <Chip 
                          label={
                            job.timePeriod === 'day' ? 'Últ. 24h' : 
                            job.timePeriod === 'week' ? 'Últ. Semana' : 'Últ. Mês'
                          } 
                          size="small" 
                          variant="outlined" 
                          color="info" 
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
                        {job.type === 'social' ? (
                          <>
                            {job.platform === 'youtube' && <YouTubeIcon sx={{ fontSize: 16, color: '#FF0000' }} />}
                            {job.platform === 'instagram' && <InstagramIcon sx={{ fontSize: 16, color: '#E1306C' }} />}
                            {job.platform === 'facebook' && <FacebookIcon sx={{ fontSize: 16, color: '#4267B2' }} />}
                            {job.platform === 'twitter' && <TwitterIcon sx={{ fontSize: 16, color: '#1DA1F2' }} />}
                            {job.platform === 'tiktok' && <PlanetIcon sx={{ fontSize: 16 }} />}
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>@{job.handle}</Typography>
                          </>
                        ) : (
                          <>
                            <WebsiteIcon sx={{ fontSize: 16 }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{job.targetDomain}</Typography>
                          </>
                        )}
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                        <Typography variant="body2">"{job.keywords}"</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, mt: 1, alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CronIcon sx={{ fontSize: 14 }} /> {job.cronSchedule}
                        </Typography>
                        {job.lastRunAt && (
                          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <HistoryIcon sx={{ fontSize: 14 }} /> Última: {new Date(job.lastRunAt).toLocaleString()}
                          </Typography>
                        )}
                        {job.pendingCount > 0 && (
                          <Button 
                            size="small" 
                            variant="outlined" 
                            color="info" 
                            startIcon={<ViewIcon />}
                            onClick={() => handleOpenResults(job)}
                            sx={{ height: 24, fontSize: '0.7rem' }}
                          >
                            {job.pendingCount} Pendentes
                          </Button>
                        )}
                      </Box>
                    </Box>
                  }
                />
                <ListItemSecondaryAction sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Resultados Encontrados">
                    <IconButton onClick={() => handleOpenResults(job)} color={job.pendingCount > 0 ? "warning" : "primary"}>
                      <Badge badgeContent={job.pendingCount} color="error">
                        <ViewIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Limpar Histórico">
                    <IconButton onClick={(e) => handleOpenCleanMenu(e, job._id)}>
                      <CleanIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Executar busca agora">
                    <IconButton 
                      onClick={() => handleRunNow(job._id)} 
                      disabled={runningJob === job._id || !job.isActive}
                      color="primary"
                    >
                      {runningJob === job._id ? <CircularProgress size={24} /> : <RunIcon />}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={job.isActive ? "Pausar" : "Ativar"}>
                    <IconButton onClick={() => handleToggle(job._id)}>
                      {job.isActive ? <PauseIcon /> : <RefreshIcon />}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Editar">
                    <IconButton onClick={() => handleEditClick(job)} color="primary">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Excluir">
                    <IconButton onClick={() => handleDelete(job._id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            </Paper>
          ))}
          {jobs.length === 0 && !showAdd && (
            <Box sx={{ ...insetSurfaceSx, textAlign: 'center', py: 4, borderStyle: 'dashed' }}>
              <Typography color="text.secondary">Nenhuma rotina de descoberta configurada.</Typography>
              <Button sx={{ ...buttonSx, mt: 1 }} size="small" onClick={() => setShowAdd(true)}>Cadastrar a Primeira</Button>
            </Box>
          )}
        </List>
      )}

      {/* Modal de Resultados Pendentes */}
      <Dialog 
        open={resultsModalOpen} 
        onClose={() => setResultsModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Revisar Links Descobertos: {viewingJob?.name}</Typography>
            <IconButton onClick={() => setResultsModalOpen(false)}><RejectIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {resultsLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : results.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">Nenhum resultado pendente para esta rotina.</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox 
                        indeterminate={selectedResults.length > 0 && selectedResults.length < results.length}
                        checked={results.length > 0 && selectedResults.length === results.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Título Sugerido</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>URL</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      <TableSortLabel
                        active={orderBy === 'snippet'}
                        direction={orderBy === 'snippet' ? order : 'asc'}
                        onClick={() => handleRequestSort('snippet')}
                      >
                        Postado
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      <TableSortLabel
                        active={orderBy === 'foundAt'}
                        direction={orderBy === 'foundAt' ? order : 'asc'}
                        onClick={() => handleRequestSort('foundAt')}
                      >
                        Data Descoberta
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSortedResults().map((res) => (
                    <TableRow key={res._id} hover selected={selectedResults.includes(res._id)}>
                      <TableCell padding="checkbox">
                        <Checkbox 
                          checked={selectedResults.includes(res._id)}
                          onChange={() => handleSelectResult(res._id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{res.title}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ 
                            maxWidth: 300, 
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            fontSize: '0.8rem',
                            color: 'primary.main'
                          }}>
                            {res.url}
                          </Box>
                          <IconButton size="small" component={Link} href={res.url} target="_blank">
                            <OpenIcon fontSize="inherit" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{res.snippet}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{new Date(res.foundAt).toLocaleString()}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">
            {selectedResults.length} selecionados de {results.length}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setResultsModalOpen(false)}>Fechar</Button>
            <Button 
              variant="outlined" 
              color="error" 
              startIcon={<RejectIcon />}
              disabled={!selectedResults.length || processingResults}
              onClick={handleRejectSelected}
            >
              Rejeitar Selecionados
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<ApproveIcon />}
              disabled={!selectedResults.length || processingResults}
              onClick={handleApproveSelected}
            >
              {processingResults ? <CircularProgress size={20} /> : 'Enviar p/ Captura'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Menu de Limpeza / Redescoberta */}
      <Menu
        anchorEl={cleanMenuAnchor}
        open={Boolean(cleanMenuAnchor)}
        onClose={handleCloseCleanMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => handleCleanHistory('rejected', 1)} sx={{ fontSize: '0.85rem' }}>
          <CleanIcon fontSize="inherit" sx={{ mr: 1, color: 'text.secondary' }} />
          Redescobrir Rejeitados (Última 1 hora)
        </MenuItem>
        <MenuItem onClick={() => handleCleanHistory('rejected', 24)} sx={{ fontSize: '0.85rem' }}>
          <CleanIcon fontSize="inherit" sx={{ mr: 1, color: 'text.secondary' }} />
          Redescobrir Rejeitados (Últimas 24h)
        </MenuItem>
        <MenuItem onClick={() => handleCleanHistory('rejected', 0)} sx={{ fontSize: '0.85rem' }}>
          <CleanIcon fontSize="inherit" sx={{ mr: 1, color: 'text.secondary' }} />
          Limpar Todos os Rejeitados
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleCleanHistory('all', 0)} sx={{ fontSize: '0.85rem', color: 'error.main' }}>
          <RefreshIcon fontSize="inherit" sx={{ mr: 1 }} />
          Reset Total (Limpar Tudo)
        </MenuItem>
      </Menu>

      {/* Snackbar de Feedbacks */}
      <Snackbar open={Boolean(successMsg)} autoHideDuration={4000} onClose={() => setSuccessMsg('')}>
        <Alert severity="success" sx={alertSx('success')}>{successMsg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default DorkDiscoveryTab;
